/** Client-side mirror of the PRD 15 query API shapes (server/reporting/query.ts). */
export type Granularity = "day" | "week" | "month" | "quarter" | "year" | "total";
/** What a query may ASK for; results always carry a concrete Granularity. */
export type GranularityInput = Granularity | "auto";
export type RangePreset = "last_7d" | "last_28d" | "last_90d" | "mtd" | "qtd" | "ytd" | "last_12m";
export type CompareKind = "none" | "previous_period" | "previous_year";
export type ChartKind = "line" | "bar" | "stacked" | "table" | "tile" | "cloud";

export const RANGE_PRESETS: { key: RangePreset; label: string }[] = [
  { key: "last_7d", label: "Last 7 days" },
  { key: "last_28d", label: "Last 28 days" },
  { key: "last_90d", label: "Last 90 days" },
  { key: "mtd", label: "Month to date" },
  { key: "qtd", label: "Quarter to date" },
  { key: "ytd", label: "Year to date" },
  { key: "last_12m", label: "Last 12 months" },
];
export const GRANULARITIES: { key: GranularityInput; label: string }[] = [
  { key: "auto", label: "Auto" },
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "year", label: "Year" },
  { key: "total", label: "Total" },
];

export interface ReportQuery {
  metrics: string[];
  range: RangePreset | { from: string; to: string };
  compare?: "previous_period" | "previous_year" | { from: string; to: string } | null;
  granularity?: GranularityInput;
  group_by?: string[];
  filters?: Record<string, string[]>;
}
export interface SeriesPoint {
  bucket: string;
  value: number | null;
  compare_bucket?: string;
  compare_value?: number | null;
  delta_pct?: number | null;
}
export interface Series {
  metric: string;
  label: string;
  unit: string;
  group: Record<string, string>;
  points: SeriesPoint[];
  total: number | null;
  compare_total?: number | null;
  delta_pct?: number | null;
  /** Server-resolved display names for id-like dims (category, country). */
  group_labels?: Record<string, string>;
}
export interface ReportResult {
  range: { from: string; to: string };
  compare_range: { from: string; to: string } | null;
  granularity: Granularity;
  series: Series[];
  freshness: Record<
    string,
    { computed_through: string | null; last_run_at: string | null; last_error: string | null }
  >;
  is_partial: boolean;
  /** Server-side adjustments worth showing (grain coarsened, groups truncated). */
  notes?: string[];
  /** USD reference rates (display only) for the non-USD currencies in a money result. */
  fx?: { as_of: string; source: string; usd_per_unit: Record<string, number> };
}

export interface MetricInfo {
  key: string;
  family: string;
  label: string;
  description: string;
  unit: string;
  dims: readonly string[];
  settle_days: number;
  kind: "event" | "snapshot";
  watermark: {
    computed_through: string | null;
    last_run_at: string | null;
    last_rows: number | null;
    last_duration_ms: number | null;
    last_error: string | null;
  } | null;
}
export interface DerivedInfo {
  key: string;
  family: string;
  label: string;
  description: string;
  numerator: string;
  denominator: string;
  unit: string;
}
export interface Registry {
  dims: readonly string[];
  metrics: MetricInfo[];
  derived: DerivedInfo[];
}

export interface Widget {
  key: string;
  title: string;
  kind: ChartKind;
  metrics: string[];
  group_by?: string[];
  filters?: Record<string, string[]>;
  width?: 1 | 2;
  description?: string;
  /** Pinned range start (lifetime widgets): [since, yesterday] regardless of the page range. */
  since?: string;
}
export interface Dashboard {
  slug: string;
  title: string;
  description: string;
  widgets: Widget[];
}
export interface SavedReport {
  report_definition_id: string;
  slug: string;
  category: string;
  title: string;
  description: string | null;
  spec: ReportQuery & { chart?: ChartKind };
  version: number;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** The global filter state a dashboard/explore page keeps in its URL. */
export interface ReportFilterState {
  range: RangePreset | "custom";
  from?: string;
  to?: string;
  compare: CompareKind;
  granularity: GranularityInput;
  filters: Record<string, string[]>;
}
export const DEFAULT_FILTER_STATE: ReportFilterState = {
  range: "last_28d",
  compare: "previous_period",
  granularity: "auto",
  filters: {},
};

export function toQueryRange(s: ReportFilterState): ReportQuery["range"] {
  return s.range === "custom" && s.from && s.to
    ? { from: s.from, to: s.to }
    : s.range === "custom"
      ? "last_28d"
      : s.range;
}
export function toQueryCompare(s: ReportFilterState): ReportQuery["compare"] {
  return s.compare === "none" ? null : s.compare;
}

/** Money in a currency's own symbol/code ("$12.99", "CA$12.99", "£4.99", "AED 19.99"); minor
 *  units scaled by the currency's own decimals (JPY 0, KWD 3). Unknown / blank → "$" (USD). */
export function formatMoney(minor: number, currency?: string | null): string {
  const code = currency && /^[A-Za-z]{3}$/.test(currency) ? currency.toUpperCase() : "USD";
  try {
    const f = new Intl.NumberFormat("en-US", { style: "currency", currency: code });
    const digits = f.resolvedOptions().maximumFractionDigits ?? 2;
    return f.format(minor / 10 ** digits);
  } catch {
    return `$${(minor / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  }
}

/** A minor-unit amount in another currency as USD minor units at a reference rate, or null. */
export function toUsdMinor(minor: number, currency: string, usdPerUnit: number): number | null {
  try {
    const digits =
      new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
    return Math.round((minor / 10 ** digits) * usdPerUnit * 100);
  } catch {
    return null;
  }
}

/** Display text for a dimension value: yes/no dims read as words, not "true"/"false". */
const BOOL_DIM_LABELS: Record<string, [string, string]> = {
  is_first_time: ["First-time", "Returning"],
  is_trial: ["Trial", "Direct"],
  is_new_user: ["New", "Existing"],
};
export function dimValueLabel(dim: string, value: string | undefined): string {
  if (value === undefined) return "";
  if (value === "true" || value === "false") {
    const [yes, no] = BOOL_DIM_LABELS[dim] ?? ["Yes", "No"];
    return value === "true" ? yes : no;
  }
  return value;
}

export function formatValue(v: number | null, unit: string, currency?: string | null): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  switch (unit) {
    case "minor":
      return formatMoney(v, currency);
    case "pct":
      return `${v.toFixed(2)} %`;
    case "ratio":
      return v.toLocaleString("en-US", { maximumFractionDigits: 3 });
    case "ms":
      return v >= 3_600_000
        ? `${(v / 3_600_000).toFixed(1)} h`
        : v >= 60_000
          ? `${(v / 60_000).toFixed(1)} min`
          : `${Math.round(v).toLocaleString()} ms`;
    default:
      return Number.isInteger(v)
        ? v.toLocaleString("en-US")
        : v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
}
export function formatBucket(bucket: string, g: Granularity): string {
  const d = new Date(`${bucket}T00:00:00Z`);
  const o: Intl.DateTimeFormatOptions = { timeZone: "UTC" };
  switch (g) {
    case "day":
      return d.toLocaleDateString("en-US", { ...o, month: "short", day: "numeric" });
    case "week":
      return `wk ${d.toLocaleDateString("en-US", { ...o, month: "short", day: "numeric" })}`;
    case "month":
      return d.toLocaleDateString("en-US", { ...o, month: "short", year: "2-digit" });
    case "quarter":
      return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`;
    case "year":
      return String(d.getUTCFullYear());
    case "total":
      return "Total";
  }
}
/** Column headers for dims whose key reads like a column name. */
export const DIM_HEADERS: Record<string, string> = {
  card_category_id: "Category",
  country: "Country",
  acquisition_source: "Acquisition source",
  reminder_type: "Reminder type",
  is_group_card: "Group card",
};
export function seriesName(s: Series, metricLabel = s.label): string {
  const g = Object.entries(s.group)
    .map(([d, v]) => s.group_labels?.[d] ?? dimValueLabel(d, v))
    .filter(Boolean)
    .join(" · ");
  return g ? `${metricLabel} — ${g}` : metricLabel;
}
